import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchShipments, fetchReports } from '../services/shipments';
import ShipmentCard from '../components/shipments/ShipmentCard';
import { Plus, FileText, PlayCircle, ArrowUpRight } from 'lucide-react';

export default function Dashboard() {
  const [shipments, setShipments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchShipments(), fetchReports()])
      .then(([s, r]) => {
        setShipments(Array.isArray(s) ? s : []);
        setReports(Array.isArray(r) ? r : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const shipmentList = Array.isArray(shipments) ? shipments : [];
  const reportList = Array.isArray(reports) ? reports : [];

  const stats = {
    total: shipmentList.length,
    blocking: shipmentList.filter(s => s.status === 'blocking').length,
    warning: shipmentList.filter(s => s.status === 'warning').length,
    pass: shipmentList.filter(s => s.status === 'pass').length,
  };

  const featured = shipmentList.find(s => s.reference === 'CTQ-2026-0041') || shipmentList[0];

  if (loading) {
    return (
      <div className="space-y-8" data-testid="dashboard-page">
        <div className="rounded-xl border border-white/10 bg-navy-900 p-5 text-sm text-slate-400" data-testid="dashboard-loading">
          Loading dashboard...
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <StatSkeleton key={i} />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skel key={i} />)}
        </div>
        <div className="rounded-xl border border-white/10 bg-navy-900 p-8">
          <div className="h-4 w-40 rounded bg-white/10 animate-pulse mb-4" />
          <div className="h-28 rounded bg-white/5 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Active shipments" value={stats.total} accent="text-white" testid="stat-total" />
        <Stat label="Blocking risks" value={stats.blocking} accent="text-red-400" testid="stat-blocking" />
        <Stat label="Validation warnings" value={stats.warning} accent="text-amber-400" testid="stat-warning" />
        <Stat label="Compliant" value={stats.pass} accent="text-emerald-400" testid="stat-pass" />
      </div>

      {/* Workflow CTA strip */}
      <div className="rounded-xl border border-cyan-glow/20 bg-cyan-glow/5 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-glow font-mono mb-2">Pre-shipment review workflow</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">
              Create shipment → Generate checklist → Upload docs → Validate → Report
            </h2>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl">
              Walk through a full validation cycle. AI surfaces guidance, rules decide compliance state, humans review.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/shipments?new=1" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover transition-colors" data-testid="dashboard-create-shipment-btn">
              <Plus className="h-4 w-4" />
              Create shipment
            </Link>
            {featured && (
              <Link to={`/shipments/${featured.id}?tab=summary`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/15 hover:bg-white/5 transition-colors" data-testid="dashboard-run-validation-btn">
                <PlayCircle className="h-4 w-4" />
                Run validation
              </Link>
            )}
            {featured && (
              <Link to={`/shipments/${featured.id}?tab=report`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/10 hover:bg-white/5 transition-colors" data-testid="dashboard-view-report-btn">
                <FileText className="h-4 w-4" />
                View report
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Active shipments */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold tracking-tight">Active shipments</h2>
          <Link to="/shipments" className="text-sm text-cyan-glow hover:text-cyan-hover inline-flex items-center gap-1" data-testid="dashboard-shipments-all">
            All shipments <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {shipmentList.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-navy-900 p-10 text-center">
            <div className="font-display text-lg text-white mb-2">No shipments yet</div>
            <p className="text-sm text-slate-400 mb-5">Create your first shipment to start a pre-shipment review.</p>
            <Link to="/shipments?new=1" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover" data-testid="empty-create-shipment">
              <Plus className="h-4 w-4" />Create shipment
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shipmentList.slice(0, 6).map(s => <ShipmentCard key={s.id} shipment={s} />)}
          </div>
        )}
      </div>

      {/* Reports preview */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold tracking-tight">Recent reports</h2>
          <Link to="/reports" className="text-sm text-cyan-glow hover:text-cyan-hover inline-flex items-center gap-1" data-testid="dashboard-reports-all">
            All reports <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="rounded-xl border border-white/10 bg-navy-900 overflow-hidden">
          {reportList.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No reports generated yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-navy-800/50 border-b border-white/5">
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider font-mono">
                  <th className="px-5 py-3">Reference</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Open issues</th>
                  <th className="px-5 py-3">Generated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reportList.slice(0, 4).map(r => (
                  <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 font-mono text-xs">{r.shipment_reference}</td>
                    <td className="px-5 py-3 capitalize">
                      <span className={
                        r.status === 'pass' ? 'text-emerald-400' :
                        r.status === 'warning' ? 'text-amber-400' :
                        r.status === 'blocking' ? 'text-red-400' : 'text-slate-300'
                      }>{r.status}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-300">{r.open_issues}</td>
                    <td className="px-5 py-3 text-slate-400 text-xs font-mono">{new Date(r.generated_at).toLocaleString()}</td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/shipments/${r.shipment_id}?tab=report`} className="text-cyan-glow hover:text-cyan-hover text-xs">View →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-navy-900 p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${accent}`}>{value}</div>
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="rounded-xl border border-white/10 bg-navy-900 p-5">
      <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
      <div className="h-8 w-16 rounded bg-white/5 animate-pulse mt-3" />
    </div>
  );
}

function Skel() {
  return <div className="h-44 rounded-xl border border-white/10 bg-navy-900 animate-pulse" />;
}
